import React, { createElement, type ReactNode, Suspense, useEffect, useRef, useSyncExternalStore } from 'react';

import type { ModalType } from '../../../props';

import type { ModalHost } from '../../model/modalHost';
import { getModalHostInternals } from '../../model/modalHostInternals';
import { ModalErrorBoundary } from '../ModalErrorBoundary/ModalErrorBoundary';
import { ModalLoadError, type ModalRegistryEntry } from '../../../registry';

interface ModalHostItemProps {
  host: ModalHost;
  id: string;
  type: ModalType;
  fallback: ReactNode;
}

export const ModalHostItem: React.FC<ModalHostItemProps> = ({ host, id, type, fallback }) => {
  const { instances, unresolvedTimeoutMs, reportError, discardModal } = getModalHostInternals(host);
  const entry = useSyncExternalStore(host.registry.subscribe, () => host.registry.getEntry(type));
  const props = instances.getProps(id) as Record<string, unknown> | undefined;
  const resolvedTypeRef = useRef<ModalType | null>(null);
  const componentRef = useRef<ModalRegistryEntry['component'] | null>(null);

  if (entry && componentRef.current === null) componentRef.current = entry.component;

  useEffect(() => {
    if (!props) {
      reportError(new Error(`Modal "${id}" of type "${type}" has no props.`), {
        reason: 'unresolved',
        modalType: type,
        modalId: id,
      });

      discardModal(id);

      return undefined;
    }

    if (entry) {
      resolvedTypeRef.current = type;

      return undefined;
    }

    if (resolvedTypeRef.current === type) {
      discardModal(id);

      return undefined;
    }

    const timeoutId = setTimeout(() => {
      reportError(new Error(`Modal type "${type}" is not registered.`), {
        reason: 'unresolved',
        modalType: type,
        modalId: id,
      });

      discardModal(id);
    }, unresolvedTimeoutMs);

    return () => clearTimeout(timeoutId);
  }, [entry, props, id, type, reportError, unresolvedTimeoutMs, discardModal]);

  const handleClose = () => host.closeModal(id);

  const handleRenderError = (error: unknown) => {
    const reason = error instanceof ModalLoadError ? 'load' : 'render';

    reportError(error, { reason, modalType: type, modalId: id });
    discardModal(id);
  };

  if (!entry || !props || !componentRef.current) return null;

  return (
    <ModalErrorBoundary onError={handleRenderError}>
      <Suspense fallback={fallback}>
        {createElement(componentRef.current, { ...props, open: true, onClose: handleClose })}
      </Suspense>
    </ModalErrorBoundary>
  );
};
